import { Flex, Icon, IconName, Text } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useId, useState } from 'react'

import * as Styled from './styles'

interface ImportedInvoiceDetailAccordionProps {
  children: ReactNode
  defaultOpen?: boolean
  icon?: IconName
  subtitle?: ReactNode
  title: ReactNode
}

/** A collapsible section of the detail panel. Each one keeps its own state, so several can be open at once. */
export const ImportedInvoiceDetailAccordion = ({
  children,
  defaultOpen = false,
  icon,
  subtitle,
  title,
}: ImportedInvoiceDetailAccordionProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const bodyId = useId()

  const handleHeaderClick = useCallback(() => {
    setIsOpen((wasOpen) => !wasOpen)
  }, [])

  return (
    <Flex as="section" flexDirection="column">
      <Styled.ImportedInvoiceDetailAccordionHeader
        aria-controls={bodyId}
        aria-expanded={isOpen}
        onClick={handleHeaderClick}
        type="button"
      >
        {icon && <Icon icon={icon} />}
        <Flex as="span" flexDirection="column" flexGrow={1}>
          <Text as="span" variant="bodyMD" weight="medium">
            {title}
          </Text>
          {subtitle && (
            <Text as="span" colorVariant="discreet" variant="bodySM" weight="medium">
              {subtitle}
            </Text>
          )}
        </Flex>
        <Styled.ImportedInvoiceDetailAccordionChevron>
          <Icon icon={isOpen ? 'chevronUp' : 'chevronDown'} />
        </Styled.ImportedInvoiceDetailAccordionChevron>
      </Styled.ImportedInvoiceDetailAccordionHeader>
      <Styled.ImportedInvoiceDetailAccordionBody hidden={!isOpen} id={bodyId}>
        {children}
      </Styled.ImportedInvoiceDetailAccordionBody>
    </Flex>
  )
}
